import EventEmitter from 'eventemitter3';
import * as jsoncParser from 'jsonc-parser';
import React, { useCallback, useEffect, useRef } from 'react';

import { JSONObject } from 'src/app/graph/explorerPage/helpers/types';
import { useStudioEditor } from 'src/hooks/useStudioEditor';

import {
  GraphIdentifier,
  usePerGraphIdentifierLocalStorage,
} from '../usePerGraphIdentifierLocalStorage';

const emitter = new EventEmitter();
/**
 * Because the environment variables are rendered in explorer, and the preflight modal at the same time,
 * we need a way to have a way to update the explorer editor when the modal is finished,
 * this function will make all instances of the environment editor update to the current localStorage value
 */
export function updateAllEnvironmentVariableEditors() {
  emitter.emit('update');
}

export const useEnvironmentVariables = ({
  localStorageGraphIdentifier,
  compactEditor,
}: {
  localStorageGraphIdentifier: GraphIdentifier | null;
  compactEditor: boolean;
}) => {
  const [environmentVariablesValue, setEnvironmentVariablesValue] =
    usePerGraphIdentifierLocalStorage({
      key: 'studioVariantEnvironmentVariables',
      graphIdentifier: localStorageGraphIdentifier,
      stableInitialValue: '',
    });

  const environmentVariablesEditor = useStudioEditor({
    language: 'json',
    defaultValue: environmentVariablesValue,
    contextmenu: compactEditor ? false : undefined,
    lineNumbers: compactEditor ? 'off' : undefined,
    glyphMargin: compactEditor ? false : undefined,
  });

  React.useEffect(() => {
    const disposable = environmentVariablesEditor.onDidChangeModelContent(
      () => {
        const value = environmentVariablesEditor.getValue();
        return setTimeout(() => setEnvironmentVariablesValue?.(value));
      },
    );
    return () => disposable.dispose();
  }, [environmentVariablesEditor, setEnvironmentVariablesValue]);

  // handle when somewhere besides the monaco editor wants to set the value of env vars
  const handleSetEnvironmentVariables = useCallback(
    (newEnvVarValues: JSONObject) => {
      environmentVariablesEditor?.setValue(
        JSON.stringify(newEnvVarValues, null, '  '),
      );
    },
    [environmentVariablesEditor],
  );

  const environmentVariablesValueRef = useRef(environmentVariablesValue);
  environmentVariablesValueRef.current = environmentVariablesValue;
  useEffect(() => {
    const handler = () => {
      environmentVariablesEditor.setValue(environmentVariablesValueRef.current);
    };
    emitter.on('update', handler);
    return () => {
      emitter.off('update', handler);
    };
  }, [environmentVariablesEditor]);

  const getEnvironmentVariables = useCallback(() => {
    return jsoncParser.parse(environmentVariablesEditor.getValue()) ?? {};
  }, [environmentVariablesEditor]);
  return {
    getEnvironmentVariables,
    setEnvironmentVariables: handleSetEnvironmentVariables,
    environmentVariablesEditor,
  };
};
